import { Button } from "@/components/ui/button";
import { ShieldCheck, Zap, CreditCard, ArrowRight, Scale } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneMockup } from "@/components/PhoneMockup";
import { scrollToId } from "@/lib/api";
import { HERO } from "@/constants/testIds";

const trust = [
  { icon: CreditCard, label: "Pagamento PayPal" },
  { icon: Zap, label: "Attivazione rapida" },
  { icon: Scale, label: "Trasparente e legale" },
];

export const Hero = () => {
  const reduce = useReducedMotion();
  const fade = (d = 0) => ({
    initial: reduce ? { opacity: 0 } : { opacity: 0, y: 22 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, delay: d, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <section id="home-hero" data-testid={HERO.section} className="relative overflow-hidden">
      <div className="absolute inset-0 hero-glow" />
      <div className="absolute inset-0 grid-pattern opacity-60" />
      <div className="container-x relative pt-12 pb-20 sm:pt-16 sm:pb-28">
        <div className="flex justify-center">
          <motion.div
            initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.9, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mb-10 sm:mb-12"
          >
            <motion.div
              animate={reduce ? {} : { y: [0, -8, 0] }}
              transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
              className="rounded-3xl bg-white p-3 shadow-xl ring-1 ring-black/5 glow-strong"
            >
              <img src="/logo.png" alt="Controlla Telefoni" className="h-28 w-28 sm:h-36 sm:w-36 object-contain" />
            </motion.div>
          </motion.div>
        </div>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          <div>
            <motion.div {...fade(0)} className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground glow">
              <ShieldCheck className="h-3.5 w-3.5 text-accent" />
              Controllo parentale Android professionale
            </motion.div>

            <motion.h1 {...fade(0.08)} className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
              Proteggi la tua famiglia online dal tuo{" "}
              <span className="neon-text">pannello cloud</span>
            </motion.h1>

            <motion.p {...fade(0.16)} className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
              Chiamate, messaggi, app, posizione GPS e tempo di utilizzo. Tutto sotto controllo, in tempo reale,
              in modo trasparente e con il consenso di chi usa il dispositivo. Scegli un abbonamento o un telefono già configurato.
            </motion.p>

            <motion.div {...fade(0.24)} className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button size="lg" className="rounded-xl glow-hover h-12 px-7 text-base" data-testid={HERO.primary} onClick={() => scrollToId("prezzi")}>
                Vedi i piani <ArrowRight className="h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" className="rounded-xl h-12 px-7 text-base" data-testid={HERO.secondary} onClick={() => scrollToId("come-funziona")}>
                Come funziona
              </Button>
            </motion.div>

            <motion.div {...fade(0.32)} className="mt-9 flex flex-wrap gap-x-6 gap-y-3">
              {trust.map((t) => (
                <div key={t.label} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <t.icon className="h-4 w-4 text-accent" />
                  {t.label}
                </div>
              ))}
            </motion.div>
          </div>

          <div className="relative">
            <PhoneMockup />
          </div>
        </div>
      </div>
    </section>
  );
};
